// Each color is a design-system CSS custom property with a hardcoded hex
// fallback. We use the design-system *semantic* palette (--blue, --green, …),
// which is reliably defined on :root, rather than the theme colors
// (--primary/--secondary) which are business-branding tokens not guaranteed to
// be present in the widget runtime. The fallback covers the case where a token
// still doesn't resolve, so a source never collapses to an empty color.
const DESIGN_SYSTEM_COLORS = {
  blue: { token: '--blue', fallback: '#478fff' },
  green: { token: '--green', fallback: '#54d28c' },
  purple: { token: '--purple', fallback: '#bd98f7' },
  orange: { token: '--orange', fallback: '#ff8f00' },
  muted: { token: '--gray-lighten-1', fallback: '#e0e0e0' },
  neutral: { token: '--neutral', fallback: '#586e8c' },
};

const readCssColor = (colorConfig) => {
  if (!colorConfig) return '';
  const { token, fallback } = colorConfig;
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return fallback;
  }
  const value = window.getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  return value || fallback;
};

const SOURCE_COLOR_TOKENS = {
  website: DESIGN_SYSTEM_COLORS.blue,
  facebook: DESIGN_SYSTEM_COLORS.green,
  facebook_messenger: DESIGN_SYSTEM_COLORS.green,
  direct: DESIGN_SYSTEM_COLORS.orange,
  direct_link_scheduler: DESIGN_SYSTEM_COLORS.orange,
  ai: DESIGN_SYSTEM_COLORS.green,
  ai_receptionist: DESIGN_SYSTEM_COLORS.green,
  email: DESIGN_SYSTEM_COLORS.purple,
  google: DESIGN_SYSTEM_COLORS.green,
  other: DESIGN_SYSTEM_COLORS.muted,
  unknown: DESIGN_SYSTEM_COLORS.neutral,
};

// Colors resolve lazily (at call time), not at module load: the CSS custom
// properties may not be on :root yet when this module is first imported (early
// code-split chunk, embedded widget, SSR), so reading them at render time picks
// up the theme once it's mounted — and the hex fallbacks above cover the case
// where a token never resolves.
// Distinct colors for the top sources, assigned by rank (index). Ordered so the
// first few sources are visually well separated.
const SOURCE_ROW_TOKENS = [
  DESIGN_SYSTEM_COLORS.blue,
  DESIGN_SYSTEM_COLORS.green,
  DESIGN_SYSTEM_COLORS.purple,
  DESIGN_SYSTEM_COLORS.orange,
  DESIGN_SYSTEM_COLORS.muted,
];

const resolveSourceColor = (key) => readCssColor(SOURCE_COLOR_TOKENS[key]);

const getSourceColor = (source = {}) => source.color
  || resolveSourceColor(source.color_key)
  || resolveSourceColor(source.source_key)
  || resolveSourceColor(source.source_key?.split('_')[0])
  || resolveSourceColor('unknown');

const getSourceRowColor = (index) => readCssColor(SOURCE_ROW_TOKENS[index]) || resolveSourceColor('unknown');

const buildLeadsWidgetChartData = ({ buckets = [], sources = [] } = {}) => {
  const labels = buckets.map((bucket) => bucket.label);
  const sourceKeys = sources.map((source) => source.source_key);

  return {
    labels,
    datasets: sourceKeys.map((sourceKey) => {
      const source = sources.find((item) => item.source_key === sourceKey) || {};

      return {
        label: source.label || source.fallback_label || sourceKey,
        source_key: sourceKey,
        data: buckets.map((bucket) => bucket.sources?.[sourceKey] || 0),
        backgroundColor: getSourceColor(source),
        stack: 'leads',
      };
    }),
  };
};

const hasLeadsChartData = ({ buckets = [] } = {}) => buckets.some((bucket) => (
  Object.values(bucket.sources || {}).some((value) => value > 0)
));

export {
  getSourceColor,
  getSourceRowColor,
  buildLeadsWidgetChartData,
  hasLeadsChartData,
};
